import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAuthContext } from '../../context/AuthContext';
import PasswordInput from './PasswordInput';
import { handleAxiosAuthError } from '../../utils/AxiosErrorHandling';
import apiClient from '../../api/apiClient'; // ← اضافه شد برای درخواست‌های reset

export default function AuthModal() {
    const { login, register, isModalOpen, closeAuthModal } = useAuthContext();

    // حالت اصلی: 'login', 'register', 'reset'
    const [mode, setMode] = useState('login');
    // مرحله در حالت reset: 'request' یا 'confirm'
    const [resetStep, setResetStep] = useState('request');

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');

    // فیلدهای مخصوص reset
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [localIsLoading, setLocalIsLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [registerSuccess, setRegisterSuccess] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);

    // ریست کردن خطاها هنگام جابجایی بین لاگین و ثبت‌نام
    useEffect(() => {
        setApiError(null);
    }, [mode, resetStep]);

    if (!isModalOpen) return null;

    // ──────────────────────────────────────────────────
    // توابع کمکی برای بهینه‌سازی شماره موبایل ایران
    // ──────────────────────────────────────────────────
    const normalizePhoneNumber = (input) => {
        // تبدیل اعداد فارسی/عربی به انگلیسی
        let normalized = input.replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
        normalized = normalized.replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString());
        // حذف کاراکترهای غیرعددی
        normalized = normalized.replace(/\D/g, '');
        if (normalized.startsWith('98') && normalized.length === 12) {
            normalized = '0' + normalized.slice(2);
        }
        if (normalized.startsWith('9') && normalized.length === 10) {
            normalized = '0' + normalized;
        }
        return normalized;
    };

    // تشخیص اینکه identifier از نوع ایمیل است یا شماره موبایل
    const getIdentifierType = (value) => {
        const trimmed = value.trim();
        if (trimmed.includes('@')) return 'email';
        // اگر حداقل ۱۱ رقم باشد و با 09 شروع شود یا بعد از نرمال‌سازی ۱۱ رقم شود
        const phone = normalizePhoneNumber(trimmed);
        if (phone.length === 11 && phone.startsWith('09')) return 'phone';
        return 'unknown';
    };

    // ──────────────────────────────────────────────────
    // توابع مربوط به بازیابی رمز عبور (RESET)
    // ──────────────────────────────────────────────────
    const handleResetRequest = async (e) => {
        e.preventDefault();
        const rawIdentifier = identifier.trim();
        if (!rawIdentifier) {
            setApiError('لطفاً ایمیل یا شماره موبایل خود را وارد کنید.');
            return;
        }

        // بهینه‌سازی شماره موبایل
        let finalIdentifier = rawIdentifier;
        if (getIdentifierType(rawIdentifier) === 'phone') {
            finalIdentifier = normalizePhoneNumber(rawIdentifier);
        }

        setLocalIsLoading(true);
        setApiError(null);
        try {
            await apiClient.post('/auth/password-reset/request/', { identifier: finalIdentifier });
            // در صورت موفقیت (حتی اگر کاربر وجود نداشته باشد) به مرحله confirm برو
            setResetStep('confirm');
            setApiError(null);
        } catch (err) {
            // اگر خطای ۴۰۰ یا ۴۰۴ از سرور آمد، پیام مناسب نمایش بده
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            if (serverMsg) {
                setApiError(serverMsg);
            } else if (err.message) {
                setApiError(err.message);
            } else {
                setApiError('درخواست با خطا مواجه شد. لطفاً دوباره تلاش کنید.');
            }
            // اگر identifier ایمیل بود و خطا خورد، می‌توانیم پیشنهاد استفاده از شماره موبایل بدهیم
            if (getIdentifierType(rawIdentifier) === 'email') {
                setApiError(prev => prev + ' (در صورت ثبت‌نام با شماره موبایل، لطفاً شماره خود را وارد کنید)');
            }
        } finally {
            setLocalIsLoading(false);
        }
    };

    const handleResetConfirm = async (e) => {
        e.preventDefault();
        const rawIdentifier = identifier.trim();
        if (!rawIdentifier) {
            setApiError('شناسه (ایمیل یا شماره موبایل) یافت نشد. لطفاً دوباره درخواست کد دهید.');
            return;
        }
        if (!otpCode.trim()) {
            setApiError('لطفاً کد تأیید را وارد کنید.');
            return;
        }
        if (!newPassword) {
            setApiError('رمز عبور جدید را وارد کنید.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setApiError('رمز عبور جدید و تکرار آن مطابقت ندارند.');
            return;
        }
        if (newPassword.length < 6) {
            setApiError('رمز عبور باید حداقل ۶ کاراکتر باشد.');
            return;
        }

        let finalIdentifier = rawIdentifier;
        if (getIdentifierType(rawIdentifier) === 'phone') {
            finalIdentifier = normalizePhoneNumber(rawIdentifier);
        }

        setLocalIsLoading(true);
        setApiError(null);
        try {
            await apiClient.post('/auth/password-reset/confirm/', {
                identifier: finalIdentifier,
                otp_code: otpCode.trim(),
                password: newPassword,        // اصلاح شده: new_password -> password
                password2: confirmPassword,   // اضافه شده: password2 برای تأیید رمز
            });
            setResetSuccess(true);
            // پس از ۲ ثانیه به صفحه ورود برگرد
            setTimeout(() => {
                setMode('login');
                setResetStep('request');
                setResetSuccess(false);
                setOtpCode('');
                setNewPassword('');
                setConfirmPassword('');
                setIdentifier('');
                setApiError(null);
            }, 2000);
        } catch (err) {
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            if (serverMsg) {
                setApiError(serverMsg);
            } else if (err.message) {
                setApiError(err.message);
            } else {
                setApiError('تأیید کد با خطا مواجه شد. لطفاً دوباره تلاش کنید.');
            }
        } finally {
            setLocalIsLoading(false);
        }
    };

    const goBackToLogin = () => {
        setMode('login');
        setResetStep('request');
        setApiError(null);
        setResetSuccess(false);
        setOtpCode('');
        setNewPassword('');
        setConfirmPassword('');
    };

    // ──────────────────────────────────────────────────
    // توابع قبلی (ورود / ثبت‌نام) بدون تغییر
    // ──────────────────────────────────────────────────
    const handleClose = () => {
        setApiError(null);
        setRegisterSuccess(false);
        setResetSuccess(false);
        setIdentifier('');
        setPassword('');
        setFullName('');
        setOtpCode('');
        setNewPassword('');
        setConfirmPassword('');
        setMode('login');
        setResetStep('request');
        closeAuthModal();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanIdentifier = identifier.trim();
        const cleanFullName = fullName.trim();

        if (!cleanIdentifier || !password || (mode === 'register' && !cleanFullName)) {
            setApiError("لطفاً تمام فیلدها را به درستی تکمیل کنید.");
            return;
        }

        setLocalIsLoading(true);
        setApiError(null);

        try {
            if (mode === 'login') {
                await login(cleanIdentifier, password, false);
            } else {
                const isEmail = cleanIdentifier.includes('@');
                const phone = isEmail ? null : cleanIdentifier;
                const email = isEmail ? cleanIdentifier : null;
                await register(phone, email, password, cleanFullName);
                setRegisterSuccess(true);
                setMode('login');
                setPassword('');
                setApiError(null);
            }
        } catch (err) {
            console.error("Auth Error:", err);
            if (err.message) {
                setApiError(err.message);
            } else {
                setApiError("خطایی در سیستم رخ داده است. لطفا دوباره تلاش کنید.");
            }
        } finally {
            setLocalIsLoading(false);
        }
    };

    // ──────────────────────────────────────────────────
    // توابع رندر بخش‌های مختلف مودال
    // ──────────────────────────────────────────────────
    const renderResetRequest = () => (
        <form onSubmit={handleResetRequest} className="space-y-5">
            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">
                    ایمیل یا شماره موبایل
                </label>
                <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    required
                    placeholder="شماره موبایل یا ایمیل"
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left transition-all bg-gray-50/50"
                    dir="ltr"
                />
            </div>
            {apiError && (
                <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl">
                    <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium leading-relaxed text-right">{apiError}</span>
                </div>
            )}
            <button
                type="submit"
                disabled={localIsLoading}
                className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
            >
                {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : 'ارسال کد تأیید'}
            </button>
            <button
                type="button"
                onClick={goBackToLogin}
                className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
            >
                ← بازگشت به صفحه ورود
            </button>
        </form>
    );

    const renderResetConfirm = () => (
        <form onSubmit={handleResetConfirm} className="space-y-5">
            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">کد تأیید</label>
                <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    required
                    placeholder="کد احراز هویت"
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left bg-gray-50/50"
                    dir="ltr"
                />
            </div>
            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">رمز عبور جدید</label>
                <PasswordInput
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    showValidation={false}
                />
            </div>
            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">تکرار رمز عبور جدید</label>
                <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    showValidation={false}
                />
            </div>
            {resetSuccess && (
                <div className="flex items-center gap-3 p-4 text-[11px] text-green-800 bg-green-50 border-r-4 border-green-500 rounded-xl">
                    <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
                    <span className="font-medium">رمز عبور با موفقیت تغییر کرد. به صفحه ورود هدایت می‌شوید...</span>
                </div>
            )}
            {apiError && (
                <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl">
                    <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium leading-relaxed text-right">{apiError}</span>
                </div>
            )}
            <button
                type="submit"
                disabled={localIsLoading || resetSuccess}
                className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
            >
                {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : 'تغییر رمز عبور'}
            </button>
            <button
                type="button"
                onClick={() => {
                    setResetStep('request');
                    setApiError(null);
                    setOtpCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                }}
                className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
            >
                ← درخواست مجدد کد
            </button>
        </form>
    );

    const renderAuthForm = () => (
        <form onSubmit={handleSubmit} className="space-y-5">
            {registerSuccess && (
                <div className="flex items-center gap-3 p-4 text-[11px] text-green-800 bg-green-50 border-r-4 border-green-500 rounded-xl">
                    <CheckCircle2 size={20} className="text-green-600 flex-shrink-0" />
                    <span className="font-medium">ثبت‌نام موفق! حالا می‌توانید وارد شوید.</span>
                </div>
            )}

            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">ایمیل یا شماره تماس</label>
                <input
                    type="text"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    required
                    placeholder="شماره موبایل یا ایمیل"
                    className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-left transition-all bg-gray-50/50"
                    dir="ltr"
                />
            </div>

            {mode === 'register' && (
                <div className="space-y-1.5 animate-in fade-in slide-in-from-right-2 duration-300">
                    <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">نام و نام خانوادگی</label>
                    <input
                        type="text"
                        value={fullName}
                        onChange={e => setFullName(e.target.value)}
                        required
                        placeholder="مثال: علی محمدی"
                        className="w-full px-4 py-3 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-purple-600 focus:border-transparent outline-none text-right bg-gray-50/50"
                    />
                </div>
            )}

            <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-700 text-right mr-1">رمز عبور</label>
                <PasswordInput
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    showValidation={mode === 'register'}
                />
            </div>

            {apiError && (
                <div className="flex items-start gap-3 p-4 text-[11px] text-red-700 bg-red-50 border-r-4 border-red-500 rounded-xl animate-shake">
                    <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium leading-relaxed text-right">{apiError}</span>
                </div>
            )}

            <button
                type="submit"
                disabled={localIsLoading}
                className="w-full py-4 rounded-2xl font-bold text-white bg-purple-700 hover:bg-purple-800 active:scale-[0.98] transition-all disabled:bg-purple-300 shadow-lg shadow-purple-100 flex items-center justify-center"
            >
                {localIsLoading ? <Loader2 size={24} className="animate-spin" /> : (mode === 'login' ? 'ورود به حساب' : 'تأیید و ثبت‌نام')}
            </button>

            {/* دکمه فراموشی رمز (فقط در حالت ورود) */}
            {mode === 'login' && (
                <button
                    type="button"
                    onClick={() => {
                        setMode('reset');
                        setResetStep('request');
                        setApiError(null);
                        setRegisterSuccess(false);
                        setResetSuccess(false);
                    }}
                    className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
                >
                    رمز عبور خود را فراموش کرده‌اید؟
                </button>
            )}

            <button
                type="button"
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="w-full text-center text-[11px] font-bold text-purple-700 hover:text-purple-900 transition-colors pt-2"
            >
                {mode === 'login' ? 'هنوز عضو نیستید؟ ایجاد حساب کاربری' : 'قبلاً ثبت‌نام کرده‌اید؟ وارد شوید'}
            </button>
        </form>
    );

    // ──────────────────────────────────────────────────
    // رندر اصلی مودال
    // ──────────────────────────────────────────────────
    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm rtl px-4"
            onClick={handleClose}
        >
            <div
                className="relative w-full max-w-md p-8 bg-white rounded-3xl shadow-2xl transition-all scale-100"
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={handleClose}
                    className="absolute top-5 left-5 text-gray-400 hover:text-red-500 transition-colors p-1"
                >
                    <X size={22} />
                </button>

                <h2 className="text-2xl font-extrabold text-gray-900 text-right mb-8">
                    {mode === 'login' && 'خوش آمدید'}
                    {mode === 'register' && 'ساخت حساب کاربری'}
                    {mode === 'reset' && (resetStep === 'request' ? 'بازیابی رمز عبور' : 'تغییر رمز عبور')}
                </h2>

                {mode === 'reset' ? (
                    resetStep === 'request' ? renderResetRequest() : renderResetConfirm()
                ) : (
                    renderAuthForm()
                )}
            </div>
        </div>
    );
}